import { useTraderText } from '../context/TraderLanguageContext';
import { TraderLanguageSelect } from './TraderLanguageSelect';
/**
 * Mobile Views for WebTrader
 * Contains mobile-optimized versions of Deposit, Withdraw, Calendar, Settings pages
 */
import { useState, useEffect, useCallback } from 'react';
import { useTraderAuth, traderAPI } from '../context/TraderAuthContext';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { 
  CreditCard, Building2, Wallet, CheckCircle, AlertCircle, ChevronLeft, 
  ArrowDownCircle, ArrowUpCircle, Calendar, Clock, Globe, Filter, 
  ChevronDown, AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw, 
  Star, User, Lock, Bell, Shield, Smartphone, Languages, Eye, EyeOff,
  Upload, FileText, Check, X, Camera, Sun, Moon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== DEPOSIT VIEW ====================
export const MobileDepositView = ({ onBack }) => {
  const tx = useTraderText();
  const { user } = useTraderAuth();
  const { isDark } = useTraderTheme();
  const { language } = useTraderLanguage();
  
  const [selectedMethod, setSelectedMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  const depositMethods = [
    { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, minAmount: 100, maxAmount: 100000 },
    { id: 'credit_card', label: 'Credit/Debit Card', icon: CreditCard, minAmount: 50, maxAmount: 10000 },
    { id: 'crypto', label: 'Cryptocurrency', icon: Wallet, minAmount: 50, maxAmount: 50000 },
  ];

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await traderAPI.get('/api/trader/me');
        const tradeAccounts = response.data.trade_accounts || [];
        setAccounts(tradeAccounts);
        if (tradeAccounts.length > 0) {
          setSelectedAccount(tradeAccounts[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };
    fetchAccounts();
  }, []);

  const selectedMethodData = depositMethods.find(m => m.id === selectedMethod);
  const currency = user?.currency || 'USD';
  const currencySymbol = currency === 'TRY' ? '₺' : '$';

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount <= 0) {
      toast.error(tx('Please enter a valid amount'));
      return;
    }

    if (numAmount < selectedMethodData.minAmount) {
      toast.error(tx(`Minimum amount: ${currencySymbol}${selectedMethodData.minAmount}`));
      return;
    }

    setLoading(true);
    
    try {
      const response = await traderAPI.post(`/api/trader/deposits`, {
        amount: numAmount,
        account_id: selectedAccount,
        method: selectedMethod,
        currency: currency
      });

      if (response.data) {
        setStep(3);
        toast.success(tx('Deposit request created'));
      }
    } catch (error) {
      toast.error(tx(error.response?.data?.detail || 'Deposit failed'));
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-4 flex items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'}`}>
        <button onClick={onBack} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          <ChevronLeft size={24} />
        </button>
        <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Deposit")}</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '100px' }}>
        {step === 1 && (
          <>
            {/* Account Selection */}
            {accounts.length > 0 && (
              <div>
                <label className={`text-sm mb-2 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Select Account")}</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                >
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_number} - {acc.currency} ({tx(acc.account_type)})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Payment Methods */}
            <div>
              <label className={`text-sm mb-3 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Payment Method")}</label>
              <div className="space-y-3">
                {depositMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedMethod(method.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        selectedMethod === method.id
                          ? isDark ? 'border-cyan-500 bg-cyan-500/10' : 'border-blue-500 bg-blue-50'
                          : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        selectedMethod === method.id
                          ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-100 text-blue-600'
                          : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon size={24} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx(method.label)}</p>
                        <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{tx("Min: ")}{currencySymbol}{method.minAmount}{tx(" - Max: ")}{currencySymbol}{method.maxAmount.toLocaleString(tx('en-US'))}
                        </p>
                      </div>
                      {selectedMethod === method.id && (
                        <CheckCircle className="text-cyan-500" size={20} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className={`text-sm mb-2 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Amount")}</label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-10 pr-4 py-4 text-2xl rounded-xl ${
                    isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'
                  } border focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                />
              </div>
              
              {/* Quick Amounts */}
              <div className="flex flex-wrap gap-2 mt-3">
                {quickAmounts.map((qa) => (
                  <button
                    key={qa}
                    onClick={() => setAmount(qa.toString())}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      amount === qa.toString()
                        ? isDark ? 'bg-cyan-500 text-white' : 'bg-blue-500 text-white'
                        : isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {currencySymbol}{qa.toLocaleString(tx('en-US'))}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={() => setStep(2)}
              disabled={!amount || parseFloat(amount) <= 0}
              className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
                amount && parseFloat(amount) > 0
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 active:scale-98'
                  : 'bg-gray-600 cursor-not-allowed'
              }`}
            >{tx("Continue ")}</button>
          </>
        )}

        {step === 2 && (
          <>
            {/* Summary */}
            <div className={`p-6 rounded-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Deposit Summary")}</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Amount")}</span>
                  <span className={`font-semibold text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {currencySymbol}{parseFloat(amount).toLocaleString(tx('en-US'))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Method")}</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{tx(selectedMethodData?.label)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Fee")}</span>
                  <span className="text-green-500">{tx("Free")}</span>
                </div>
                <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Total")}</span>
                    <span className={`font-bold text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {currencySymbol}{parseFloat(amount).toLocaleString(tx('en-US'))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className={`flex-1 py-4 rounded-xl font-semibold ${
                  isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900'
                }`}
              >{tx("Back ")}</button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500"
              >
                {tx(loading ? 'Processing...' : 'Confirm Deposit')}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <CheckCircle className="text-green-500" size={40} />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Deposit Submitted!")}</h2>
            <p className={`text-center mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Your deposit request of ")}{currencySymbol}{parseFloat(amount).toLocaleString(tx('en-US'))}{tx(" has been submitted. You will receive a confirmation email shortly. ")}</p>
            <button
              onClick={onBack}
              className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500"
            >{tx("Back to Home ")}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== WITHDRAW VIEW ====================
export const MobileWithdrawView = ({ onBack }) => {
  const tx = useTraderText();
  const { user } = useTraderAuth();
  const { isDark } = useTraderTheme();
  
  const [selectedMethod, setSelectedMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountHolder: '',
    iban: '',
    swiftCode: ''
  });
  const [savedBanks,setSavedBanks]=useState([]);
  const [selectedBank,setSelectedBank]=useState('');
  useEffect(()=>{const bank=savedBanks.find(b=>b.id===selectedBank);setBankDetails({bankName:bank?.bank_name||'',accountHolder:bank?.account_holder||'',iban:bank?.iban||'',swiftCode:bank?.swift||''});},[selectedBank,savedBanks]);

  const withdrawMethods = [
    { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, minAmount: 100, fee: 0 },
  ];

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await traderAPI.get('/api/trader/me');
        const tradeAccounts = response.data.trade_accounts || [];
        setAccounts(tradeAccounts);
        if (tradeAccounts.length > 0) {
          setSelectedAccount(tradeAccounts[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };
    fetchAccounts();
  }, []);

  const selectedMethodData = withdrawMethods.find(m => m.id === selectedMethod);
  const currency = accounts.find(a=>a.id===selectedAccount)?.currency||user?.currency||'USD';
  const currencySymbol = {USD:'$',EUR:'€',GBP:'£',TRY:'₺'}[currency]||currency;
  const balance = accounts.find(a=>a.id===selectedAccount)?.balance??user?.balance??0;
  useEffect(()=>{let live=true;traderAPI.get('/api/trader/bank-accounts').then(({data})=>{if(live){setSavedBanks(data);setSelectedBank(data[0]?.id||'');}}).catch(()=>toast.error(tx('Could not load bank accounts')));return()=>{live=false;};},[tx]);

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount <= 0) {
      toast.error(tx('Please enter a valid amount'));
      return;
    }

    if (numAmount > balance) {
      toast.error(tx('Insufficient balance'));
      return;
    }

    setLoading(true);
    
    try {
      let bankId=selectedBank;
      if(!bankId){
        const existing=savedBanks.find(b=>b.iban===''.concat(bankDetails.iban).replace(/\s/g,'').toUpperCase());
        if(existing)bankId=existing.id;
        else{const added=await traderAPI.post('/api/trader/bank-accounts',{bank_name:bankDetails.bankName,account_holder:bankDetails.accountHolder,iban:bankDetails.iban,swift:bankDetails.swiftCode,currency});bankId=added.data.id;setSavedBanks(rows=>[...rows,added.data]);setSelectedBank(bankId);}
      }
      const response = await traderAPI.post(`/api/trader/withdrawals`, {
        amount: numAmount,
        account_id: selectedAccount,
        method: selectedMethod,
        currency: currency,
        bank_account_id: bankId
      });

      if (response.data) {
        setStep(3);
        toast.success(tx('Withdrawal request created'));
      }
    } catch (error) {
      const detail=error.response?.data?.detail;
      toast.error(tx(typeof detail==='string'?detail:Array.isArray(detail)?detail.map(v=>v.msg).join('; '):'Withdrawal failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-4 flex items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'}`}>
        <button onClick={onBack} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          <ChevronLeft size={24} />
        </button>
        <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Withdraw")}</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '100px' }}>
        {/* Available Balance */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-r from-cyan-50 to-blue-50'}`}>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Available Balance")}</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {currencySymbol}{balance.toLocaleString(tx('en-US'))}
          </p>
        </div>

        {step === 1 && (
          <>
            {/* Withdrawal Methods */}
            <div>
              <label className={`text-sm mb-3 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Withdrawal Method")}</label>
              <div className="space-y-3">
                {withdrawMethods.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setSelectedMethod(method.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                        selectedMethod === method.id
                          ? isDark ? 'border-cyan-500 bg-cyan-500/10' : 'border-blue-500 bg-blue-50'
                          : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        selectedMethod === method.id
                          ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-100 text-blue-600'
                          : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <Icon size={24} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx(method.label)}</p>
                        <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{tx("Min: ")}{currencySymbol}{method.minAmount}{tx(" | Fee: Free ")}</p>
                      </div>
                      {selectedMethod === method.id && (
                        <CheckCircle className="text-cyan-500" size={20} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount Input */}
            <div>
              <label className={`text-sm mb-2 block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Amount")}</label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full pl-10 pr-4 py-4 text-2xl rounded-xl ${
                    isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'
                  } border focus:outline-none focus:ring-2 focus:ring-cyan-500`}
                />
              </div>
              <button
                onClick={() => setAmount(balance.toString())}
                className={`mt-2 text-sm ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}
              >{tx("Withdraw All ")}</button>
            </div>

            {/* Bank Details for Bank Transfer */}
            {selectedMethod === 'bank_transfer' && (
              <div className="space-y-4">
                <label className={`text-sm block ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Bank Details")}</label>
                <input
                  type="text"
                  placeholder={tx("Bank Name")}
                  value={bankDetails.bankName}
                  data-testid="mobile-withdraw-bank-name"
                  disabled={Boolean(selectedBank)}
                  onChange={(e) => setBankDetails({...bankDetails, bankName: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
                <input
                  type="text"
                  placeholder={tx("Account Holder Name")}
                  value={bankDetails.accountHolder}
                  data-testid="mobile-withdraw-bank-holder"
                  disabled={Boolean(selectedBank)}
                  onChange={(e) => setBankDetails({...bankDetails, accountHolder: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
                <input
                  type="text"
                  placeholder="IBAN"
                  value={bankDetails.iban}
                  data-testid="mobile-withdraw-bank-iban"
                  disabled={Boolean(selectedBank)}
                  onChange={(e) => setBankDetails({...bankDetails, iban: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
                <input
                  type="text"
                  placeholder="SWIFT/BIC Code"
                  value={bankDetails.swiftCode}
                  data-testid="mobile-withdraw-bank-swift"
                  disabled={Boolean(selectedBank)}
                  onChange={(e) => setBankDetails({...bankDetails, swiftCode: e.target.value})}
                  className={`w-full px-4 py-3 rounded-xl ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
                />
              </div>
            )}

            <label className="block text-sm">{tx("Bank Account")}<select data-testid="mobile-withdraw-bank-select" className={`w-full mt-2 px-3 py-3 rounded border min-w-0 ${isDark?'bg-gray-800 text-white border-gray-700':'bg-white text-gray-900'}`} value={selectedBank} onChange={e=>setSelectedBank(e.target.value)}><option value="">{tx("New bank account")}</option>{savedBanks.map(bank=><option key={bank.id} value={bank.id}>{`${bank.bank_name} · ${bank.iban.slice(-8)}`}</option>)}</select></label>
            {/* Submit Button */}
            <button
              onClick={() => setStep(2)}
              data-testid="mobile-withdraw-continue"
              disabled={!amount || parseFloat(amount) <= 0}
              className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
                amount && parseFloat(amount) > 0
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 active:scale-98'
                  : 'bg-gray-600 cursor-not-allowed'
              }`}
            >{tx("Continue ")}</button>
          </>
        )}

        {step === 2 && (
          <>
            {/* Summary */}
            <div className={`p-6 rounded-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Withdrawal Summary")}</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Amount")}</span>
                  <span className={`font-semibold text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {currencySymbol}{parseFloat(amount).toLocaleString(tx('en-US'))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Method")}</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{tx(selectedMethodData?.label)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{tx("Processing Time")}</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{tx("1-3 Business Days")}</span>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className={`p-4 rounded-xl flex items-start gap-3 ${isDark ? 'bg-yellow-500/10' : 'bg-yellow-50'}`}>
              <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
              <p className={`text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-800'}`}>{tx("Please ensure your bank details are correct. Incorrect details may delay your withdrawal. ")}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className={`flex-1 py-4 rounded-xl font-semibold ${
                  isDark ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-900'
                }`}
              >{tx("Back ")}</button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500"
                data-testid="mobile-withdraw-confirm"
              >
                <span data-testid="mobile-withdraw-confirm-label">{tx(loading ? 'Processing...' : 'Confirm Withdrawal')}</span>
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <CheckCircle className="text-green-500" size={40} />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Withdrawal Submitted!")}</h2>
            <p className={`text-center mb-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Your withdrawal request of ")}{currencySymbol}{parseFloat(amount).toLocaleString(tx('en-US'))}{tx(" is being processed. Expected processing time: 1-3 business days. ")}</p>
            <button
              onClick={onBack}
              className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500"
            >{tx("Back to Home ")}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ECONOMIC CALENDAR VIEW ====================
export const MobileCalendarView = ({ onBack }) => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImpact, setSelectedImpact] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');

  useEffect(() => {
    fetchEconomicEvents();
  }, []);

  const fetchEconomicEvents = async () => {
    setLoading(true);
    try {
      // Try to fetch from our backend API
      const response = await fetch(`${API_URL}/api/economic-calendar`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      } else {
        // Fallback to mock data
        setEvents(getMockEvents());
      }
    } catch (error) {
      console.error('Failed to fetch economic calendar:', error);
      setEvents(getMockEvents());
    } finally {
      setLoading(false);
    }
  };

  const getMockEvents = () => {
    const now = new Date();
    const baseEvents = [
      { id: 1, country: 'US', flag: '🇺🇸', currency: 'USD', event: 'Non-Farm Payrolls', impact: 'high', time: '13:30', previous: '227K', forecast: '180K', actual: null },
      { id: 2, country: 'US', flag: '🇺🇸', currency: 'USD', event: 'Unemployment Rate', impact: 'high', time: '13:30', previous: '4.2%', forecast: '4.1%', actual: null },
      { id: 3, country: 'EU', flag: '🇪🇺', currency: 'EUR', event: 'ECB Interest Rate Decision', impact: 'high', time: '12:45', previous: '4.50%', forecast: '4.50%', actual: null },
      { id: 4, country: 'UK', flag: '🇬🇧', currency: 'GBP', event: 'GDP q/q', impact: 'medium', time: '07:00', previous: '0.1%', forecast: '0.2%', actual: '0.3%' },
      { id: 5, country: 'JP', flag: '🇯🇵', currency: 'JPY', event: 'BoJ Policy Rate', impact: 'high', time: '03:00', previous: '0.25%', forecast: '0.25%', actual: null },
      { id: 6, country: 'DE', flag: '🇩🇪', currency: 'EUR', event: 'ZEW Economic Sentiment', impact: 'medium', time: '10:00', previous: '15.7', forecast: '17.5', actual: null },
      { id: 7, country: 'US', flag: '🇺🇸', currency: 'USD', event: 'CPI m/m', impact: 'high', time: '13:30', previous: '0.3%', forecast: '0.2%', actual: null },
      { id: 8, country: 'AU', flag: '🇦🇺', currency: 'AUD', event: 'Employment Change', impact: 'medium', time: '00:30', previous: '35.6K', forecast: '25.0K', actual: null },
      { id: 9, country: 'CA', flag: '🇨🇦', currency: 'CAD', event: 'BoC Interest Rate Decision', impact: 'high', time: '14:00', previous: '5.00%', forecast: '5.00%', actual: null },
      { id: 10, country: 'US', flag: '🇺🇸', currency: 'USD', event: 'Retail Sales m/m', impact: 'medium', time: '13:30', previous: '0.4%', forecast: '0.3%', actual: null },
    ];
    
    return baseEvents.map((e, i) => ({
      ...e,
      date: new Date(now.getTime() + (i - 3) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    }));
  };

  const filteredEvents = events.filter(e => {
    if (selectedImpact !== 'all' && e.impact !== selectedImpact) return false;
    if (selectedCountry !== 'all' && e.country !== selectedCountry) return false;
    return true;
  });

  const groupedEvents = filteredEvents.reduce((acc, event) => {
    const date = event.date || 'Today';
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-4 flex items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'}`}>
        <button onClick={onBack} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          <ChevronLeft size={24} />
        </button>
        <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Economic Calendar")}</h1>
        <button onClick={fetchEconomicEvents} className={`ml-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filters */}
      <div className={`px-4 py-3 flex gap-2 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <select
          value={selectedImpact}
          onChange={(e) => setSelectedImpact(e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
        >
          <option value="all">{tx("All Impact")}</option>
          <option value="high">{tx("High")}</option>
          <option value="medium">{tx("Medium")}</option>
          <option value="low">{tx("Low")}</option>
        </select>
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'} border`}
        >
          <option value="all">{tx("All Countries")}</option>
          <option value="US">{tx("🇺🇸 United States")}</option>
          <option value="EU">{tx("🇪🇺 Europe")}</option>
          <option value="UK">{tx("🇬🇧 United Kingdom")}</option>
          <option value="JP">{tx("🇯🇵 Japan")}</option>
          <option value="AU">{tx("🇦🇺 Australia")}</option>
          <option value="CA">{tx("🇨🇦 Canada")}</option>
        </select>
      </div>

      {/* Events List */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '100px' }}>
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <RefreshCw className="animate-spin text-cyan-500" size={32} />
          </div>
        ) : (
          Object.entries(groupedEvents).map(([date, dateEvents]) => (
            <div key={date}>
              {/* Date Header */}
              <div className={`px-4 py-2 sticky top-0 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {new Date(date).toLocaleDateString(tx('en-US'), { weekday: 'long', month: 'short', day: 'numeric' })}
                </span>
              </div>
              
              {/* Events */}
              {dateEvents.map((event) => (
                <div 
                  key={event.id}
                  className={`px-4 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Impact Indicator */}
                    <div className={`w-1.5 h-full min-h-[60px] rounded-full ${getImpactColor(event.impact)}`} />
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{event.flag}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {event.currency}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {event.time}
                        </span>
                      </div>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx(event.event)}</p>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Prev: ")}<span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{event.previous}</span>
                        </span>
                        <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Fcst: ")}<span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{event.forecast}</span>
                        </span>
                        {event.actual && (
                          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>{tx("Act: ")}<span className="text-green-500 font-medium">{event.actual}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ==================== SETTINGS VIEW ====================
export const MobileSettingsView = ({ onBack }) => {
  const tx = useTraderText();
  const { user, refreshUser } = useTraderAuth();
  const { isDark, toggleTheme } = useTraderTheme();
  const { language, setLanguage } = useTraderLanguage();
  
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showAccountsModal, setShowAccountsModal] = useState(false);

  // Profile Form State
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    country: user?.country || '',
    city: user?.city || '',
    address: user?.address || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password Form State
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurPass, setShowCurPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [changingPass, setChangingPass] = useState(false);

  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    trading: true,
    marketing: false
  });

  // Sync profile data when opening modal
  useEffect(() => {
    if (showEditProfileModal && user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        country: user.country || '',
        city: user.city || '',
        address: user.address || '',
      });
    }
  }, [showEditProfileModal, user]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const response = await traderAPI.put('/api/trader/profile', profileForm);
      if (response.data?.success || response.status === 200) {
        toast.success(tx('Profile updated successfully'));
        if (refreshUser) refreshUser();
        setShowEditProfileModal(false);
      }
    } catch (err) {
      toast.error(tx(err.response?.data?.detail || 'Failed to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwords.current_password) {
      toast.error(tx('Please enter your current password'));
      return;
    }
    if (passwords.new_password.length < 6) {
      toast.error(tx('New password must be at least 6 characters'));
      return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error(tx('New passwords do not match'));
      return;
    }

    setChangingPass(true);
    try {
      const response = await traderAPI.put('/api/trader/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      if (response.data?.success || response.status === 200) {
        toast.success(tx('Password changed successfully'));
        setPasswords({ current_password: '', new_password: '', confirm_password: '' });
        setShowPasswordModal(false);
      }
    } catch (err) {
      toast.error(tx(err.response?.data?.detail || 'Failed to change password'));
    } finally {
      setChangingPass(false);
    }
  };

  const settingsSections = [
    {
      title: 'Account',
      items: [
        { id: 'profile', icon: User, label: 'Edit Profile', action: () => setShowEditProfileModal(true) },
        { id: 'password', icon: Lock, label: 'Change Password', action: () => setShowPasswordModal(true) },
        { id: 'security', icon: Shield, label: 'Security Settings', action: () => setShowSecurityModal(true) },
        { id: 'accounts', icon: Wallet, label: 'Trading Accounts', action: () => setShowAccountsModal(true) },
      ]
    },
    {
      title: 'Preferences',
      items: [
        { id: 'language', icon: Languages, label: 'Language', value: language === 'en' ? 'English' : 'Türkçe', action: () => setLanguage(language === 'en' ? 'tr' : 'en') },
        { id: 'theme', icon: isDark ? Sun : Moon, label: 'Theme', value: isDark ? 'Dark' : 'Light', action: toggleTheme },
      ]
    },
    {
      title: 'Notifications',
      items: [
        { id: 'email_notif', icon: Bell, label: 'Email Notifications', toggle: true, value: notifications.email, action: () => setNotifications({...notifications, email: !notifications.email}) },
        { id: 'push_notif', icon: Smartphone, label: 'Push Notifications', toggle: true, value: notifications.push, action: () => setNotifications({...notifications, push: !notifications.push}) },
        { id: 'trading_alerts', icon: TrendingUp, label: 'Trading Alerts', toggle: true, value: notifications.trading, action: () => setNotifications({...notifications, trading: !notifications.trading}) },
      ]
    }
  ];

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-4 flex items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'}`}>
        <button onClick={onBack} data-testid="mobile-settings-back" aria-label={tx('Back')} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          <ChevronLeft size={24} />
        </button>
        <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Settings")}</h1>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '100px' }}>
        {/* User Info Card */}
        <div className={`m-4 p-4 rounded-xl border ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-md ${
              isDark ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-blue-100 text-blue-600 border border-blue-200'
            }`}>
              {user?.full_name?.[0] || 'T'}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-bold text-lg truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {user?.full_name || 'Trader'}
              </p>
              <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {user?.email || 'trader@example.com'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {tx("Verified Account")}
                </span>
                <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  #{user?.account_id || user?.id?.slice(0, 8)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Sections */}
        {settingsSections.map((section) => (
          <div key={section.title} className="mb-6">
            <h2 className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {tx(section.title)}
            </h2>
            <div className={`border-y ${isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-white border-gray-200'}`}>
              {section.items.map((item, idx) => {
                const Icon = item.icon;
                if (item.id === 'language') {
                  return (
                    <div key={item.id} className={`w-full flex items-center justify-between gap-3 px-4 py-4 border-b ${isDark ? 'border-gray-700/60' : 'border-gray-100'}`}>
                      <label htmlFor="mobile-settings-language" className={`flex items-center gap-4 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        <Icon size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                        {tx(item.label)}
                      </label>
                      <TraderLanguageSelect id="mobile-settings-language"/>
                    </div>
                  );
                }
                return (
                  <button
                    key={item.id}
                    onClick={item.action}
                    className={`w-full flex items-center justify-between px-4 py-4 ${
                      idx !== section.items.length - 1 ? `border-b ${isDark ? 'border-gray-700/60' : 'border-gray-100'}` : ''
                    } ${isDark ? 'active:bg-gray-700/50' : 'active:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <Icon size={20} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx(item.label)}</span>
                    </div>
                    {item.toggle ? (
                      <div className={`w-12 h-7 rounded-full p-1 transition-colors ${item.value ? 'bg-cyan-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}>
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${item.value ? 'translate-x-5' : ''}`} />
                      </div>
                    ) : item.value ? (
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx(item.value)}</span>
                    ) : (
                      <ChevronDown size={18} className={`-rotate-90 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ================= EDIT PROFILE MODAL ================= */}
      {showEditProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className={`w-full max-w-md max-h-[90vh] rounded-2xl border shadow-2xl flex flex-col overflow-hidden ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <User size={18} className="text-cyan-500" />
                <h3 className="font-bold text-base">{tx("Edit Profile")}</h3>
              </div>
              <button onClick={() => setShowEditProfileModal(false)} className="p-1">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleProfileSave} className="p-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Email")}</label>
                <input
                  type="email"
                  disabled
                  value={user?.email || ''}
                  className={`w-full p-2.5 rounded-xl border text-sm opacity-60 cursor-not-allowed ${
                    isDark ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'
                  }`}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("First Name")}</label>
                  <input
                    type="text"
                    value={profileForm.first_name}
                    onChange={e => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Last Name")}</label>
                  <input
                    type="text"
                    value={profileForm.last_name}
                    onChange={e => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Phone Number")}</label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="+90 5XX XXX XX XX"
                  className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  }`}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("City")}</label>
                  <input
                    type="text"
                    value={profileForm.city}
                    onChange={e => setProfileForm({ ...profileForm, city: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Country")}</label>
                  <input
                    type="text"
                    value={profileForm.country}
                    onChange={e => setProfileForm({ ...profileForm, country: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Address")}</label>
                <textarea
                  rows={2}
                  value={profileForm.address}
                  onChange={e => setProfileForm({ ...profileForm, address: e.target.value })}
                  className={`w-full p-2.5 rounded-xl border text-sm outline-none resize-none ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white focus:border-cyan-500' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  }`}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
                >
                  {savingProfile ? <RefreshCw size={16} className="animate-spin" /> : null}
                  {tx("Save Changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= CHANGE PASSWORD MODAL ================= */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className={`w-full max-w-md rounded-2xl border shadow-2xl flex flex-col overflow-hidden ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Lock size={18} className="text-cyan-500" />
                <h3 className="font-bold text-base">{tx("Change Password")}</h3>
              </div>
              <button onClick={() => setShowPasswordModal(false)} className="p-1">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-4 space-y-3">
              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Current Password")}</label>
                <div className="relative">
                  <input
                    type={showCurPass ? 'text' : 'password'}
                    required
                    value={passwords.current_password}
                    onChange={e => setPasswords({ ...passwords, current_password: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none pr-10 ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurPass(!showCurPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showCurPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("New Password")}</label>
                <div className="relative">
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={passwords.new_password}
                    onChange={e => setPasswords({ ...passwords, new_password: e.target.value })}
                    className={`w-full p-2.5 rounded-xl border text-sm outline-none pr-10 ${
                      isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tx("Confirm New Password")}</label>
                <input
                  type="password"
                  required
                  value={passwords.confirm_password}
                  onChange={e => setPasswords({ ...passwords, confirm_password: e.target.value })}
                  className={`w-full p-2.5 rounded-xl border text-sm outline-none ${
                    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`}
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={changingPass}
                  className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-sm shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
                >
                  {changingPass ? <RefreshCw size={16} className="animate-spin" /> : null}
                  {tx("Update Password")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= SECURITY SETTINGS MODAL ================= */}
      {showSecurityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className={`w-full max-w-md rounded-2xl border shadow-2xl flex flex-col overflow-hidden ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-cyan-500" />
                <h3 className="font-bold text-base">{tx("Security Settings")}</h3>
              </div>
              <button onClick={() => setShowSecurityModal(false)} className="p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className={`p-3 rounded-xl border flex items-center justify-between ${
                isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <p className="font-semibold text-sm">{tx("Two-Factor Authentication")}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Enhanced security for account logins")}</p>
                </div>
                <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {tx("Active")}
                </span>
              </div>

              <div className={`p-3 rounded-xl border flex items-center justify-between ${
                isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-gray-50 border-gray-200'
              }`}>
                <div>
                  <p className="font-semibold text-sm">{tx("Session Security")}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{tx("Auto logout after inactivity")}</p>
                </div>
                <span className={`text-xs font-mono font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  30 {tx("min")}
                </span>
              </div>

              <div className={`p-3 rounded-xl border ${
                isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-gray-50 border-gray-200'
              }`}>
                <p className="font-semibold text-sm mb-1">{tx("Current Device Info")}</p>
                <div className={`text-xs space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p>{tx("Platform")}: {navigator.userAgent.includes('Mobile') ? 'Mobile Web' : 'Web'}</p>
                  <p>{tx("Timezone")}: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
                  <p>{tx("Status")}: <span className="text-emerald-400 font-semibold">{tx("Protected & Encrypted")}</span></p>
                </div>
              </div>

              <button
                onClick={() => {
                  toast.success(tx('Other sessions invalidated'));
                  setShowSecurityModal(false);
                }}
                className={`w-full py-3 rounded-xl text-sm font-semibold border transition-colors ${
                  isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tx("Log Out All Other Devices")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= TRADING ACCOUNTS MODAL ================= */}
      {showAccountsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className={`w-full max-w-md rounded-2xl border shadow-2xl flex flex-col overflow-hidden ${
            isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'
          }`}>
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <Wallet size={18} className="text-cyan-500" />
                <h3 className="font-bold text-base">{tx("Trading Accounts")}</h3>
              </div>
              <button onClick={() => setShowAccountsModal(false)} className="p-1">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
              {(user?.trade_accounts || []).length === 0 ? (
                <div className={`p-4 rounded-xl border text-center ${isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="font-bold text-base mb-1">#{user?.account_id || user?.id?.slice(0, 8)}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.currency || 'USD'} · 1:100</p>
                  <p className="text-lg font-bold text-emerald-400 mt-2">
                    ${Number(user?.balance || 0).toFixed(2)}
                  </p>
                </div>
              ) : (
                user.trade_accounts.map(acc => (
                  <div key={acc.id} className={`p-3 rounded-xl border flex items-center justify-between ${
                    isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">#{acc.account_id || acc.id}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                          {acc.currency}
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {acc.is_demo ? tx('Demo') : tx('Real')} · {tx('Leverage')} 1:{acc.leverage || user?.leverage || 100}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-base font-mono">
                        {Number(acc.balance || 0).toLocaleString(tx('en-US'), { minimumFractionDigits: 2 })} {acc.currency}
                      </p>
                      <span className="text-[10px] text-emerald-400 font-semibold">{tx("Active")}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DOCUMENTS VIEW ====================
export const MobileDocumentsView = ({ onBack }) => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const documentTypes = [
    { id: 'id_front', label: 'ID Card (Front)', icon: FileText, required: true },
    { id: 'id_back', label: 'ID Card (Back)', icon: FileText, required: true },
    { id: 'proof_address', label: 'Proof of Address', icon: FileText, required: true },
    { id: 'selfie', label: 'Selfie with ID', icon: Camera, required: false },
  ];

  const handleUpload = async (docType) => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', docType);
        
        const response = await traderAPI.post('/api/trader/documents/upload', formData);
        if (response.data) {
          toast.success(tx('Document uploaded successfully'));
          setDocuments([...documents, { type: docType, status: 'pending', name: file.name }]);
        }
      } catch (error) {
        toast.error(tx('Failed to upload document'));
      } finally {
        setLoading(false);
      }
    };
    
    input.click();
  };

  const getDocStatus = (docType) => {
    const doc = documents.find(d => d.type === docType);
    return doc?.status || null;
  };

  return (
    <div className={`h-full flex flex-col ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`px-4 py-4 flex items-center gap-4 border-b ${isDark ? 'border-gray-800 bg-[#0f1724]' : 'border-gray-200 bg-white'}`}>
        <button onClick={onBack} className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          <ChevronLeft size={24} />
        </button>
        <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{tx("Upload Documents")}</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '100px' }}>
        {/* Info Banner */}
        <div className={`p-4 rounded-xl ${isDark ? 'bg-cyan-500/10' : 'bg-blue-50'}`}>
          <p className={`text-sm ${isDark ? 'text-cyan-200' : 'text-blue-800'}`}>{tx("Please upload clear photos or scans of your documents for verification. Accepted formats: JPG, PNG, PDF (max 5MB each). ")}</p>
        </div>

        {/* Document Upload Cards */}
        {documentTypes.map((doc) => {
          const Icon = doc.icon;
          const status = getDocStatus(doc.id);
          
          return (
            <div
              key={doc.id}
              className={`p-4 rounded-xl border-2 border-dashed ${
                status === 'approved' 
                  ? isDark ? 'border-green-500 bg-green-500/10' : 'border-green-500 bg-green-50'
                  : status === 'pending'
                    ? isDark ? 'border-yellow-500 bg-yellow-500/10' : 'border-yellow-500 bg-yellow-50'
                    : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    status === 'approved'
                      ? 'bg-green-500/20 text-green-500'
                      : status === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {status === 'approved' ? <Check size={24} /> : <Icon size={24} />}
                  </div>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {tx(doc.label)}
                      {doc.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className={`text-sm ${
                      status === 'approved' ? 'text-green-500' :
                      status === 'pending' ? 'text-yellow-500' :
                      isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {tx(status === 'approved' ? 'Verified' : status === 'pending' ? 'Under Review' : 'Not uploaded')}
                    </p>
                  </div>
                </div>
                
                {status !== 'approved' && (
                  <button
                    onClick={() => handleUpload(doc.id)}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg font-medium text-sm ${
                      isDark ? 'bg-cyan-500 text-white' : 'bg-blue-500 text-white'
                    }`}
                  >
                    {tx(status === 'pending' ? 'Re-upload' : 'Upload')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
